import {ReactNode, useEffect, useState} from 'react';
import styled, {CSSProperties} from 'styled-components';
import {DropdownIconComponent} from '@/components/Icons';
import {Typography} from '@/components/Typography';
import {Flexbox, FlexCenter, MultiLineEllipsis, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {withComma} from '@/utils';
import {SectionLabel, Table, TableCell, TableHeaderRow, TableRow} from '../StyledComponents';

interface IProps {
  headers: Array<{
    label: any;
    width?: CSSProperties['width'];
  }>;
  data: Array<Array<any>>;
  title: string;
  sumLabel: string;
  icon?: ReactNode;
  maxLineCount?: number;
}

const SUMMARY_ROW_COUNT = 3;

export const NearbyInfoTable = ({
  title,
  sumLabel,
  headers,
  data,
  icon,
  maxLineCount,
}: IProps) => {
  const [hasViewMoreButton, setHasViewMoreButton] = useState<boolean>(
    data?.length > SUMMARY_ROW_COUNT
  );

  useEffect(() => {
    setHasViewMoreButton(data?.length > SUMMARY_ROW_COUNT);
  }, [data?.length]);

  if (!data?.length) {
    return null;
  }

  return (
    <Root>
      <Title>
        {!!icon && <IconWrapper>{icon}</IconWrapper>}
        <SectionLabel>{title}</SectionLabel>
      </Title>
      <PaddingBox top={8}>
        <Typography variant="medium12" color={palette.gray600}>
          분석 지역에 {sumLabel} {withComma(data.length)}개 있어요
        </Typography>
      </PaddingBox>
      <PaddingBox top={20}>
        <Table>
          <thead>
            <TableHeaderRow>
              {headers.map(({label, width}) => (
                <TableCell key={label} style={{width}}>
                  {label}
                </TableCell>
              ))}
            </TableHeaderRow>
          </thead>
          <tbody>
            {!!data &&
              data
                .slice(0, hasViewMoreButton ? SUMMARY_ROW_COUNT : undefined)
                .map((row, idx) => (
                  <TableRow key={`row-${idx}`}>
                    {row.map((i, cellIdx) =>
                      !maxLineCount || maxLineCount < 2 ? (
                        <TableCell key={`cell-${cellIdx}`} ellipsis>
                          {i}
                        </TableCell>
                      ) : (
                        <TableCell key={`cell-${cellIdx}`}>
                          <MultiLineEllipsis lineClamp={maxLineCount}>
                            {i}
                          </MultiLineEllipsis>
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))}
          </tbody>
        </Table>
      </PaddingBox>
      {hasViewMoreButton && (
        <PaddingBox spacing={[12, 0]}>
          <FlexCenter>
            <MoreButton onClick={() => setHasViewMoreButton(false)}>
              {withComma(data.length - SUMMARY_ROW_COUNT)}개 더보기
              <DropdownIconComponent color={palette.teal100} />
            </MoreButton>
          </FlexCenter>
        </PaddingBox>
      )}
    </Root>
  );
};

const Root = styled.div``;

const MoreButton = styled.button`
  display: flex;
  align-items: center;
  color: ${palette.teal100};
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  height: 24px;
  gap: 4px;
`;

const Title = styled(Flexbox)`
  gap: 6px;
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
`;
